// pickpocket.ts — the pickpocket staging + confirm subsystem, carved out of template.js (Phase 2b of
// plans/template-js-carve-up.md) and TS-ported for Phase 2a of plans/bundler-script-load-order-phase2.md —
// a Wave-2a fold into post-template-tail.
//
// MODULE-SCOPE SAFE: at eval only const/function defs + the 4 window attaches; no eval-time DOM access, no
// module-scope template.js reads. displayWarning is reached only at user-interaction time.
//
// PUBLIC GLOBALS (window-attached at the bottom — external callers): pickpocketStageItem (encounter-map
// DragManager drop), pickpocketConfirm / pickpocketClear (_inventory_inc.php onclick), pickpocketUpdateDC
// (template.js delegated qty handler). PICKPOCKET_ABILITY_ID / PICKPOCKET_SIZE_ORDER /
// The shared K3 weight decision supplies the preview; the size-label adapter is module-private.

import { refuseUnported } from '../janus-engine/unported';
import { calculatePickpocketWeightModifier } from '../janus-engine/port/k3-rules/pickpocket_system';

const PICKPOCKET_ABILITY_ID = '9CFBB9ED-E539-6A5F-46A6-D0DE72281AB2';

const PICKPOCKET_SIZE_ORDER = ['Tiny', 'Small', 'Medium', 'Large', 'Huge', 'Gargantuan', 'Colossal'];

function calculatePickpocketSizeMod(sizeName: string): number {
    const normalized = sizeName.charAt(0).toUpperCase() + sizeName.slice(1).toLowerCase();
    const idx = PICKPOCKET_SIZE_ORDER.indexOf(normalized);
    if (idx <= 0) return 0; // Tiny or unknown = 0
    return idx * 5;
}

function pickpocketUpdateDC(): void {
    const qtyInput = document.getElementById('pickpocket_staged_qty') as HTMLInputElement | null;
    const zone = document.getElementById('pickpocket_staging_zone');
    if (!qtyInput || !zone) return;

    const qty = Math.max(1, parseInt(qtyInput.value) || 1);
    const maxQty = parseInt(qtyInput.max) || 1;
    qtyInput.value = String(Math.min(qty, maxQty));

    const unitWeight = parseFloat(zone.dataset.staged_unit_weight ?? '') || 0;
    const sizeName = zone.dataset.staged_size || 'Tiny';
    const totalWeight = unitWeight * Math.min(qty, maxQty);

    const weightMod = calculatePickpocketWeightModifier(totalWeight);
    const sizeMod = calculatePickpocketSizeMod(sizeName);
    const totalMod = weightMod + sizeMod;

    (document.getElementById('pickpocket_staged_weight') as HTMLElement).textContent = totalWeight.toFixed(1);
    (document.getElementById('pickpocket_attention_mod') as HTMLElement).textContent = String(totalMod);
}

function pickpocketStageItem(inventoryEl: HTMLElement): void {
    const zone = document.getElementById('pickpocket_staging_zone');
    if (!zone) return;

    const hasDonning = inventoryEl.dataset.has_donning_doffing;
    if (hasDonning === '1') {
        const name = inventoryEl.dataset.inventory_name || 'This item';
        displayWarning(`${name} requires donning or doffing and cannot be pickpocketed.`);
        return;
    }

    const inventoryId = inventoryEl.dataset.inventory_id;
    const inventoryName = inventoryEl.dataset.inventory_name || 'Unknown';
    const inventoryQty = parseInt(inventoryEl.dataset.inventory_qty ?? '') || 1;
    const unitWeight = parseFloat(inventoryEl.dataset.unit_weight ?? '') || 0;
    const sizeName = inventoryEl.dataset.size || 'tiny';

    zone.dataset.staged_inventory_id = inventoryId;
    zone.dataset.staged_unit_weight = String(unitWeight);
    zone.dataset.staged_size = sizeName;

    (document.getElementById('pickpocket_staged_name') as HTMLElement).textContent = inventoryName;

    const sizeDisplay = document.getElementById('pickpocket_staged_size');
    if (sizeDisplay) {
        sizeDisplay.textContent = sizeName.charAt(0).toUpperCase() + sizeName.slice(1);
    }

    const maxQtyEl = document.getElementById('pickpocket_staged_max_qty') as HTMLElement;
    const qtyInput = document.getElementById('pickpocket_staged_qty') as HTMLInputElement;
    maxQtyEl.textContent = String(inventoryQty);
    qtyInput.max = String(inventoryQty);
    qtyInput.value = '1';

    const thumbEl = document.getElementById('pickpocket_staged_thumbnail') as HTMLElement;
    const cloned = inventoryEl.cloneNode(true) as HTMLElement;
    cloned.classList.remove('draggable');
    cloned.removeAttribute('draggable');
    cloned.style.pointerEvents = 'none';
    thumbEl.innerHTML = '';
    thumbEl.appendChild(cloned);

    (document.getElementById('pickpocket_staging_empty') as HTMLElement).classList.add('d-none');
    (document.getElementById('pickpocket_staging_content') as HTMLElement).classList.remove('d-none');

    pickpocketUpdateDC();
}

function pickpocketClear(): void {
    const zone = document.getElementById('pickpocket_staging_zone');
    if (!zone) return;

    delete zone.dataset.staged_inventory_id;
    delete zone.dataset.staged_unit_weight;
    delete zone.dataset.staged_size;

    (document.getElementById('pickpocket_staged_thumbnail') as HTMLElement).innerHTML = '';
    (document.getElementById('pickpocket_staging_empty') as HTMLElement).classList.remove('d-none');
    (document.getElementById('pickpocket_staging_content') as HTMLElement).classList.add('d-none');
}

async function pickpocketConfirm(): Promise<void> {
    const zone = document.getElementById('pickpocket_staging_zone');
    if (!zone) return;

    const inventoryId = zone.dataset.staged_inventory_id;
    if (!inventoryId) {
        console.error('No item staged for pickpocket.');
        return;
    }

    // No browser resolver currently models the theft and inventory transfer contract.
    // Keep the staged item available; refusing spends nothing and posts nothing.
    refuseUnported('pickpocket:not-ported', { abilityId: PICKPOCKET_ABILITY_ID, inventoryId, targetId: zone.dataset.target_id });
}


// ===========================================================================================
// Window attachment — the public surface (encounter-map DragManager + PHP onclick + template.js delegated
// handler). Internal consts/calc helpers stay module-local.
// ===========================================================================================
declare global {
    interface Window {
        ftClientTargetGateBlocksUse?: (
            itemId: unknown,
            casterId: unknown,
            targetId: unknown,
            encounterId: unknown,
        ) => boolean;
        pickpocketStageItem: typeof pickpocketStageItem;
        pickpocketConfirm: typeof pickpocketConfirm;
        pickpocketClear: typeof pickpocketClear;
        pickpocketUpdateDC: typeof pickpocketUpdateDC;
    }
}

window.pickpocketStageItem = pickpocketStageItem;
window.pickpocketConfirm = pickpocketConfirm;
window.pickpocketClear = pickpocketClear;
window.pickpocketUpdateDC = pickpocketUpdateDC;

export {};
